import Logger from "../utils/Logger";
import MeshClient from "../client/MeshClient";
import MeshEvents from "../constants/MeshEvents";

/**
 * The PWA-side cache of the device's offline-mode state, shared by the framework
 * (the connectivity-gate bypass in AuthInterceptor) and the per-PWA route-switch
 * chokepoints. Mirrors the native OfflineModeStateManager's state, learned two ways:
 *
 *  1. Boot PULL — the PWA sends GET_OFFLINE_MODE on startup; native replies with the
 *     current state. Mandatory: the native->PWA Mesh push has no replay, so a PWA
 *     opened after activation would otherwise never learn it is offline.
 *  2. Live PUSH — native emits OFFLINE_MODE_CHANGED on every state transition
 *     (activation and exit), so a mid-session flip is honored without a reload.
 *
 * Both arrive under eventName OFFLINE_MODE_CHANGED (native replies to the pull with
 * the same event it pushes), carried on the DOLPHIN_BUS BroadcastChannel. This store
 * owns its own listener rather than routing through MeshEventHandler's
 * request/response promise map, which only resolves an event the PWA asked for by
 * the same event name — a reply named differently from its request cannot resolve
 * there, and an unsolicited push never would.
 *
 * Fail-closed: until a reply arrives — and if none arrives within the pull timeout
 * (native version skew, handler failure) — the state stays NORMAL and every consumer
 * keeps today's online behavior byte-for-byte.
 */

// Retry cadence is kept short so all MAX_PULL_ATTEMPTS land within the consumer's
// boot-resolve cap (the window App.componentWillMount waits before falling back to NORMAL,
// ~1.5s in IEW/SEW today): 3 × 500ms. At the previous 3000ms only the first attempt fit
// that window; retries #2/#3 arrived after boot had already fallen back and could only
// correct routing post-boot. Keep this comfortably under the consumer cap if it changes.
const PULL_TIMEOUT_MS = 500;
const MAX_PULL_ATTEMPTS = 3;
const POLL_INTERVAL_MS = 50;

let offlineState = null;
let pullTimer = null;
let pullAttempts = 0;

// Fail-closed if the channel cannot be created (no BroadcastChannel in this
// environment): the state simply stays NORMAL and routing is unchanged.
try {
    const offlineBus = new BroadcastChannel('DOLPHIN_BUS');
    offlineBus.addEventListener('message', function (event) {
        try {
            const eventData = JSON.parse(event.data);
            if (eventData.eventName === MeshEvents.MESH_EVENTS.OFFLINE_MODE_CHANGED && eventData.payload) {
                applyState(typeof eventData.payload === "string"
                    ? JSON.parse(eventData.payload) : eventData.payload);
            }
        } catch (error) {
            // Not every bus message is JSON meant for us; stay silent unless it named our event.
            if (String(event.data).indexOf(MeshEvents.MESH_EVENTS.OFFLINE_MODE_CHANGED) !== -1) {
                Logger.log.warn("Offline: unparseable OFFLINE_MODE_CHANGED payload: " + error);
            }
        }
    });
} catch (error) {
    Logger.log.warn("Offline: DOLPHIN_BUS listener unavailable; offline mode stays off: " + error);
}

function applyState(state) {
    offlineState = state || null;
    if (pullTimer) {
        clearTimeout(pullTimer);
        pullTimer = null;
    }
    Logger.log.warn("Offline: mode state applied: "
        + (computeOfflineActive() ? "OFFLINE_ACTIVE" : "NORMAL"));
}

export default {

    /**
     * Pull the current state from native. Call once at PWA boot. Never rejects:
     * on timeout the state simply stays NORMAL (fail-closed to normal routing).
     */
    pullOfflineMode() {
        try {
            if (pullTimer) {
                return;
            }
            pullAttempts++;
            pullTimer = setTimeout(() => {
                pullTimer = null;
                if (offlineState !== null) {
                    return;
                }
                // Retry covers a lost/slow reply on an already-open socket (native
                // slow to answer, or a dropped OFFLINE_MODE_CHANGED frame). It does NOT
                // cover "sent before the socket opened" — the OPEN-guard below never
                // sends on a not-open socket, and the caller now pulls only after
                // connectWebSocket() resolves. Bounded, then settle on normal routing.
                if (pullAttempts < MAX_PULL_ATTEMPTS) {
                    Logger.log.warn("Offline: GET_OFFLINE_MODE pull unanswered; retry "
                        + pullAttempts + "/" + (MAX_PULL_ATTEMPTS - 1));
                    this.pullOfflineMode();
                } else {
                    Logger.log.warn("Offline: GET_OFFLINE_MODE pull unanswered after "
                        + MAX_PULL_ATTEMPTS + " attempts; staying in normal routing");
                }
            }, PULL_TIMEOUT_MS);
            // Send ONLY on an open socket: sendMessageToServer's not-open branch
            // replaces the shared singleton socket with a broken one, which would
            // drop every subsequent mesh event for the session. If the socket is
            // not open, skip; the timeout/retry (or the fail-closed default)
            // covers the outcome.
            if (MeshClient.webSocketConnection &&
                MeshClient.webSocketConnection.readyState === WebSocket.OPEN) {
                MeshClient.sendMessageToServer(JSON.stringify({
                    eventName: MeshEvents.MESH_EVENTS.GET_OFFLINE_MODE,
                    payload: ""
                }));
            } else {
                Logger.log.warn("Offline: mesh socket not open; skipping GET_OFFLINE_MODE send");
            }
        } catch (error) {
            Logger.log.warn("Offline: GET_OFFLINE_MODE pull failed: " + error);
        }
    },

    /**
     * Resolve the offline flag for a boot-time decision: return now if the pull reply
     * already arrived, else wait up to timeoutMs. Never rejects — on timeout resolves
     * fail-closed to NORMAL (false) so a caller that cannot reach native boots online.
     * pullOfflineMode() must have been called first; this only awaits the reply.
     */
    awaitOfflineModeResolved(timeoutMs) {
        // Already resolved (reply landed before this call) — no wait.
        if (offlineState !== null) {
            return Promise.resolve(computeOfflineActive());
        }
        return new Promise((resolve) => {
            let settled = false;
            const finish = () => {
                if (settled) {
                    return;
                }
                settled = true;
                clearInterval(poll);
                clearTimeout(cap);
                resolve(computeOfflineActive());
            };
            // The reply arrives on the BroadcastChannel listener (applyState), not a
            // promise we can await directly, so poll for it. A short interval keeps the
            // healthy-online wait to a few ms (native replies over loopback fast).
            const poll = setInterval(() => {
                if (offlineState !== null) {
                    finish();
                }
            }, POLL_INTERVAL_MS);
            const cap = setTimeout(finish, timeoutMs);
        });
    },

    /** Device-level flag the route-switch chokepoints and the connectivity gate read. */
    isOfflineModeActive() {
        return computeOfflineActive();
    },

    /**
     * The full cached state, or null before the first reply. Carries the offline
     * request-stamping inputs: alias (operator identity) and traceId (the activation
     * QR's jti — the id of the manager-authorized offline window).
     */
    getOfflineModeState() {
        return offlineState;
    },

    /** The activation window's traceId (=jti), or "" — stamped on offline op payloads. */
    getTraceId() {
        return (offlineState && offlineState.traceId) ? offlineState.traceId : "";
    },

    /** The operator alias captured at activation, or "" — fallback identity source. */
    getAlias() {
        return (offlineState && offlineState.alias) ? offlineState.alias : "";
    },

    /** Test seam: reset the cached state (and any pending pull timer). */
    resetForTest() {
        offlineState = null;
        pullAttempts = 0;
        if (pullTimer) {
            clearTimeout(pullTimer);
            pullTimer = null;
        }
    },

    /** Test seam: apply a state as if it arrived from native. */
    applyStateForTest(state) {
        applyState(state);
    }
};

function computeOfflineActive() {
    return !!(offlineState && offlineState.state === "OFFLINE_ACTIVE");
}
